// ============================================================
// BRAND — Single source of truth for Netcom Connect branding
// ============================================================
// Netcom Connect is a multi-product customer portal:
//   • Starlink dishes (existing)
//   • Netcom Connect SIM — white-labeled KORE Wireless OmniSIM Reach
//   • Future: routers, voice, etc.
//
// COMPLIANCE NOTE: Strings that mention Peplink or Starlink are
// constrained by the Peplink ASP Program 2.0 Term Sheet (§ 11).
// Do NOT add Starlink trademarks, SpaceX references, or any of the
// forbidden reseller/retailer/channel-provider phrasings (see the
// no-forbidden-phrasing rule in scripts/lint-compliance.js).
// The KORE relationship is wholesale-resale, not an ASP — Netcom
// purchases data wholesale, marks up, and bills the customer
// directly. We never market the customer SIM as a "KORE SIM";
// it is the "Netcom Connect SIM" (or the short form "Connect SIM"
// when the full name would wrap).

export const BRAND = {
  // Customer-facing portal name (unified across Starlink + SIM + future products)
  name: 'Netcom Connect',

  // Tagline shown next to the logo in the header. Title Case per Dillon 2026-05-29.
  tagline: 'One Login. Every Netcom Connection.',

  // Sub-headline for the login screen left panel. Title Case + explicit two-line
  // split per Dillon 2026-05-29. Rendered as two separate <span> blocks so the
  // designer can independently style each line.
  loginHeadlineLine1: 'Welcome back.',
  loginHeadlineLine2: "Let's get connected.",
  // Legacy single-string alias kept for any downstream callers that still
  // reference BRAND.loginHeadline directly.
  loginHeadline: "Welcome back. Let's get connected.",
  loginSubcopy: 'Sign in to monitor connectivity, review usage, and manage operational support across your Netcom services.',

  // Company that owns the portal
  company: 'Netcom LLC',
  companyShort: 'Netcom',

  // Domain (subdomain stays "my." — product name decouples from URL)
  domain: 'my.networksandcommunications.com',

  // Email contacts
  supportEmail: 'support@netcomedge.com',
  ordersEmail: 'orders@netcomedge.com',

  // Marketing URL of the parent site
  mainSiteUrl: 'https://networksandcommunications.com',

  // Phone (placeholder — update with real)
  phone: '1-855-9NETCOM',
};

// ============================================================
// PRODUCT CATALOG — which products this portal manages
// Source of truth for tab visibility, JWT 'products' claim,
// onboarding flow choice, and search scoping.
// ============================================================
export const PRODUCTS = {
  STARLINK: 'starlink',       // Starlink dishes (ASP via Peplink)
  SIM: 'sim',                 // Netcom Connect SIM (white-labeled KORE OmniSIM Reach)
  FIELD_SERVICES: 'field-services', // Field Services client portal (Amazon Air + future)
};

export const PRODUCT_LABELS = {
  [PRODUCTS.STARLINK]: 'Starlink',
  [PRODUCTS.SIM]: 'Netcom Connect SIM',
  [PRODUCTS.FIELD_SERVICES]: 'RF Survey Field Services',
};

// Short form for tight nav slots where the full name would wrap
export const PRODUCT_LABELS_SHORT = {
  [PRODUCTS.STARLINK]: 'Starlink',
  [PRODUCTS.SIM]: 'Connect SIM',
};

// Per-product accent colors. These drive:
//   - Sidebar section accent (left bar + colored header text)
//   - "You are viewing" product badge at the top of every product page
//   - Status dots / live indicators on product-scoped widgets
// Picked for high contrast against the navy sidebar background AND the
// white page background — same hex works in both contexts. Sky/cyan for
// Starlink (satellite, sky) and emerald for Netcom Connect SIM
// (always-on cellular, "go" / connectivity).
export const PRODUCT_ACCENTS = {
  [PRODUCTS.STARLINK]: {
    // Tailwind sky-400 / sky-500 range
    solid: '#38bdf8',      // sky-400 — sidebar accents (on dark bg)
    solidDark: '#0284c7',  // sky-600 — page badge text (on light bg)
    tint: '#e0f2fe',       // sky-100 — page badge bg
    tintBorder: '#bae6fd', // sky-200 — page badge border
    label: 'Starlink',
  },
  [PRODUCTS.SIM]: {
    // Tailwind emerald-400 / emerald-500 range
    solid: '#34d399',      // emerald-400 — sidebar accents
    solidDark: '#059669',  // emerald-600 — page badge text
    tint: '#d1fae5',       // emerald-100 — page badge bg
    tintBorder: '#a7f3d0', // emerald-200 — page badge border
    label: 'Netcom Connect SIM',
  },
  [PRODUCTS.FIELD_SERVICES]: {
    // Amazon Air orange
    solid: '#FF9900',      // sidebar accents (on dark bg)
    solidDark: '#E68900',  // page badge text (on light bg)
    tint: '#FFF7ED',       // page badge bg
    tintBorder: '#FED7AA', // page badge border
    label: 'RF Survey Field Services',
  },
};

// ============================================================
// NETCOM LOCKUP — the credential line in the footer of every page.
//
// 🚨 2026-09-10: SpaceX and Peplink notified Netcom that its ASP
// Program 2.0 status is terminated. This constant was named
// ASP_DISCLOSURE and held the old program designation; both the
// name and the value were retired that day.
//
// Do NOT swap in a satellite-reseller designation of any kind —
// the SpaceX direct-reseller agreement is under negotiation and
// UNSIGNED. Claiming it would repeat the same mistake. Change this
// only when Dillon confirms a signed contract.
//
// OEM-neutral on purpose: it survives adding or dropping any single
// manufacturer, and it claims a capability rather than a program
// status Netcom would have to defend.
// ============================================================
export const NETCOM_LOCKUP = 'Verizon Preferred Partner · Multi-OEM Connectivity Solutions';

// 🔐 MFA REQUIRED cutover date (owner decision 2026-08-12, reverses the
// 2026-06-25 "MFA optional forever" call). Grace period: every user can
// self-enroll TOTP now via /security (TotpEnrollCard); on this date the
// Cognito pool flips to Mfa.REQUIRED and Login.jsx's password path starts
// enforcing the same enrollment screen for anyone who hasn't opted in
// yet. See infra/lib/stacks/auth-stack.ts + CLAUDE.md "MFA POLICY".
// Update this string (and the auth-stack.ts deploy) together.
export const MFA_REQUIRED_DATE = 'August 26, 2026';

// Pricing disclaimer per § 3.2(a) and § 6.2
export const PRICE_DISCLAIMER = 'Prices are subject to change by Starlink at any time prior to payment processing.';

// LOA explanation copy for customer-facing screens
export const LOA_EXPLAINER = 'A Letter of Authorization (LOA) must be signed by your authorized representative directly on the Starlink Dashboard before service can be activated. Netcom cannot sign on your behalf.';

// Customer support routing copy per § 3.5, 3.6
export const SUPPORT_ROUTING = {
  technical_starlink: {
    label: 'Technical issue with your Starlink dish or connection',
    detail: 'Open a ticket directly with Starlink Support — they handle all hardware and network issues.',
    href: 'https://starlink.com/support/tickets',
    cta: 'Open Starlink Support',
  },
  technical_sim: {
    label: 'Technical issue with your Netcom Connect SIM or device connectivity',
    detail: 'Netcom handles SIM connectivity issues directly — open a ticket and our team will respond.',
    href: 'mailto:support@netcomedge.com',
    cta: 'Contact Netcom Support',
  },
  billing: {
    label: 'Billing, contracts, or installation questions',
    detail: 'Reach out to Netcom and we will help you.',
    href: 'mailto:support@netcomedge.com',
    cta: 'Contact Netcom',
  },
};

// Prohibited customer use-cases per § 4.2 — used by signup gate
// NOTE: These apply to STARLINK signups. Netcom Connect SIM has its own
// acceptable-use policy (no military/sanctioned-country deployments)
// but is not bound by the Peplink ASP § 4.2 use-case gate.
export const PROHIBITED_USE_CASES = [
  {
    id: 'residential',
    label: 'Residential or personal home use',
    detail: 'Starlink residential service should be purchased directly from Starlink.com.',
  },
  {
    id: 'federal',
    label: 'US Federal Government agency',
    detail: 'Federal procurement is handled through different channels — please contact Starlink directly.',
  },
  {
    id: 'military',
    label: 'Military, paramilitary, or intelligence use',
    detail: 'These end-uses require separate review and are not accepted through this portal.',
  },
  {
    id: 'reseller',
    label: 'Reseller / retail redistribution',
    detail: 'Service is intended for end customers only, not for resale.',
  },
];

// Theme color tokens (also exposed via Tailwind in styles/index.css)
export const COLORS = {
  primary: '#0f4c81',
  primaryDark: '#0b3d6a',
  accent: '#0ea5e9',
  success: '#10b981',
  warn: '#f59e0b',
  danger: '#ef4444',
};
